// src/components/ArtworksDashboard.jsx
import ValuationStatusLine from "./ValuationStatusLine";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ImportCSVPremium from "./ImportCSVPremium";
import { canUseImport } from "../utils/importAccess";
import { useTranslation } from "react-i18next";
import { MEDIUM_CATEGORIES } from "../utils/mediumCategory";
import { getMediumLabel, getMediumCategoryLabelI18n, getConditionLabel, getTypeOfArtLabel } from "../utils/artkivLabels";
import { applyProposalFilter, proposalCollectionIds } from "../lib/proposalExclusion";

// Enum canonique d'état de conservation (clé technique en base, calculable par l'IA).
// Même ordre que la fiche œuvre ; libellés via artwork.condition.* (source unique).
const CONDITION_FILTER_KEYS = [
  "excellent", "very_good", "good", "fair",
  "very_poor", "poor", "heavily_damaged", "partially_restorable", "irreparable",
];

function useDebounced(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}



const FALLBACK_IMG = "/images/artworks/Vignette_oeuvre_Image_Not_Available.png";

const safeParseImages = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  // In case Supabase returns object-ish
  return [];
};

const pickImageFromArray = (arr) => {
  if (!Array.isArray(arr) || !arr.length) return null;
  // Priority: explicit primary flag if present
  const primary =
    arr.find((x) => x && typeof x === "object" && (x.isPrimary || x.is_primary)) || arr[0];

  if (!primary) return null;

  // If element is a string URL/path
  if (typeof primary === "string") return primary;

  // If element is an object with url/path/file/publicUrl
  if (typeof primary === "object") {
    return primary.url || primary.path || primary.file || primary.publicUrl || primary.public_url || null;
  }
  return null;
};

const getImageFromArtwork = (a) => {
  if (!a) return FALLBACK_IMG;

  // 1) Most canonical fields first
  const directFields = ["image_url", "main_image", "cover_image", "thumbnail_url", "photo_url", "img_url"];
  for (const k of directFields) {
    const v = a?.[k];
    if (typeof v === "string" && v.trim() !== "") return v;
  }

  // 2) images array / json
  const imgs = safeParseImages(a.images);
  const fromImages = pickImageFromArray(imgs);
  if (fromImages && typeof fromImages === "string" && fromImages.trim() !== "") return fromImages;

  // 3) legacy object
  if (a.image?.path) return a.image.path;
  if (typeof a.image === "string" && a.image.trim() !== "") return a.image;

  return FALLBACK_IMG;
};

const getEstimatedDisplayValue = (artwork) => {
  const raw =
    artwork?.valuation_estimated_value ??
    artwork?.current_estimated_value ??
    artwork?.valuation_consensus ??
    artwork?.estimated_value ??
    artwork?.value;

  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const formatEuroValue = (value) =>
  value !== null ? `${value.toLocaleString()} €` : "—";

const normalizeMediumOption = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const LEGACY_MEDIUM_KEYS_TO_HIDE = new Set([
  "unknown",
  "unmatched",
  "painting_generic",
  "photography_generic",
  "paper_generic",
  "oil_paint",
  "acrylic_paint",
  "print",
  "paper",
]);

export default function ArtworksDashboard(props = {}) {
  const {
    initialCollectionId = null,
    hideCollectionFilter = false,
    isSharedCollection = false,
    collectionOwnerId = null,
    onImportDone = null,
    onStatsChange = null,
    initialArtistFilter = "",
  } = props;
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  // Ref stable pour onStatsChange — évite la boucle de re-render
  const onStatsChangeRef = useRef(onStatsChange);
  useEffect(() => { onStatsChangeRef.current = onStatsChange; }, [onStatsChange]);
  const location = useLocation();

  // ✅ Conserver le préfixe de route (évite de retomber sur /artwork/* quand on vient de /expert/*)
  const routePrefix = useMemo(() => {
    const pathname = location?.pathname || "";

    // Si on est déjà dans une zone "pro", on garde le préfixe actuel
    if (pathname.startsWith("/expert")) return "/expert";
    if (pathname.startsWith("/partner")) return "/partner";
    if (pathname.startsWith("/family-office")) return "/family-office";

    // Sinon, fallback via rôle
    const role = profile?.role;
    if (role === "expert") return "/expert";
    if (
      role === "partner" ||
      role === "partner_insurance" ||
      role === "partner_transport"
    )
      return "/partner";
    if (role === "family_office") return "/family-office";

    return "";
  }, [location?.pathname, profile?.role]);

  const [collections, setCollections] = useState([]);
  const [locations, setLocations] = useState([]); // { id, name }
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 60;

  const [collectionId, setCollectionId] = useState(initialCollectionId);
  const effectiveCollectionId = collectionId ?? initialCollectionId ?? null;

  // Scope collection : sur /collections/:id, les œuvres appartiennent souvent
  // au client propriétaire, pas au user connecté (collaborateur / expert).
  // Donc ici on filtre par collection_id, pas par client_id.
  const shouldUseCollectionScope = Boolean(effectiveCollectionId);
  const locationsOwnerId = collectionOwnerId || user?.id || null;

  const [artist, setArtist] = useState(() => initialArtistFilter || "");
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterCondition, setFilterCondition] = useState("");
  const [filterInventory, setFilterInventory] = useState("");
  const [filterMedium, setFilterMedium] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [mediumOptions, setMediumOptions] = useState([]);
  const [sort, setSort] = useState("date_desc");
  const [view, setView] = useState("grid");

  // Si la prop change (navigation entre collections)
  useEffect(() => {
    if (initialArtistFilter) setArtist(initialArtistFilter);
  }, [initialArtistFilter]);

  const dArtist = useDebounced(artist);
  const dMin = useDebounced(minValue);
  const dMax = useDebounced(maxValue);
  const dTags = useDebounced(tagsInput);
  const dLocation = useDebounced(filterLocation);
  const dCondition = useDebounced(filterCondition);
  const dInventory = useDebounced(filterInventory);
  const dMedium = useDebounced(filterMedium);
  const dCategory = useDebounced(filterCategory);

  // Reset page when filters/sort change
  useEffect(() => {
    setPage(0);
  }, [
    dArtist,
    dMin,
    dMax,
    dTags,
    dLocation,
    dCondition,
    dInventory,
    dCategory,
    dMedium,
    sort,
    effectiveCollectionId,
  ]);

  const parsedTags = useMemo(
    () => dTags.split(",").map((s) => s.trim()).filter(Boolean),
    [dTags]
  );

  const loadCollections = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("collections")
      .select("id,title")
      .eq("client_id", user.id)
      .order("title", { ascending: true });

    setCollections(data || []);
  }, [user]);

  const loadLocations = useCallback(async () => {
    if (!locationsOwnerId) return;
    const { data } = await supabase
      .from("locations")
      .select("id,name")
      .eq("client_id", locationsOwnerId);
    setLocations(data || []);
  }, [locationsOwnerId]);

  const loadMediums = useCallback(async () => {
    if (!user) return;

    try {
      let q = supabase.from("artworks").select("medium_normalized_key, medium_family_key, medium_category");

      if (shouldUseCollectionScope) {
        q = q.eq("collection_id", effectiveCollectionId);
      } else {
        q = applyProposalFilter(q.eq("client_id", user.id), await proposalCollectionIds(user.id));
      }

      if (dCategory) q = q.eq("medium_family_key", dCategory);

      const { data, error } = await q;
      if (error) throw error;

      const map = new Map();
      (data || []).forEach((row) => {
        const raw = row?.medium_normalized_key;
        const normalized = normalizeMediumOption(raw);
        if (!normalized) return;

        const key = normalized.toLowerCase();
        if (LEGACY_MEDIUM_KEYS_TO_HIDE.has(key)) return;

        if (!map.has(key)) map.set(key, normalized);
      });

      setMediumOptions(
        Array.from(map.values()).sort((a, b) => a.localeCompare(b, "fr"))
      );
    } catch (e) {
      console.error("[ArtworksDashboard] loadMediums failed:", e);
      setMediumOptions([]);
    }
  }, [user, shouldUseCollectionScope, effectiveCollectionId, dCategory]);

  const fetchArtworks = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    try {
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let q = supabase.from("artworks").select("*", { count: "exact" });

      if (shouldUseCollectionScope) {
        q = q.eq("collection_id", effectiveCollectionId);
      } else {
        q = applyProposalFilter(q.eq("client_id", user.id), await proposalCollectionIds(user.id));
      }

      if (dArtist) q = q.ilike("artist", `%${dArtist}%`);
      if (dMin && Number.isFinite(Number(dMin))) q = q.gte("value", Number(dMin));
      if (dMax && Number.isFinite(Number(dMax))) q = q.lte("value", Number(dMax));
      if (parsedTags.length) q = q.contains("tags", parsedTags);
      if (dLocation) {
        const matchedIds = locations
          .filter((l) => l.name.toLowerCase().includes(dLocation.toLowerCase()))
          .map((l) => l.id);
        if (matchedIds.length > 0) q = q.in("location_id", matchedIds);
        else q = q.eq("location_id", "00000000-0000-0000-0000-000000000000");
      }
      if (dCondition) q = q.eq("condition", dCondition);
      if (dCategory) q = q.eq("medium_family_key", dCategory);
      if (dMedium) q = q.eq("medium_normalized_key", dMedium);
      if (dInventory) q = q.ilike("inventory_number", `%${dInventory}%`);

      if (sort === "date_desc") q = q.order("created_at", { ascending: false });
      if (sort === "date_asc") q = q.order("created_at", { ascending: true });
      if (sort === "value_desc") q = q.order("value", { ascending: false });
      if (sort === "value_asc") q = q.order("value", { ascending: true });
      if (sort === "title_asc") q = q.order("title", { ascending: true });
      if (sort === "title_desc") q = q.order("title", { ascending: false });
      if (sort === "inventory_asc") q = q.order("inventory_number", { ascending: true, nullsFirst: false });

      const { data, count, error } = await q.range(from, to);
      if (error) throw error;

      const nextItems = data || [];
      const nextTotalCount = count || 0;
      setItems(nextItems);
      setTotalCount(nextTotalCount);

      // Remonte au parent exactement le compteur de la grille affichée.
      // CollectionDetail ne doit pas recalculer son propre nombre d'œuvres
      // avec une autre requête, sinon propriétaire/expert/collaborateur peuvent diverger.
      onStatsChangeRef.current?.({
        totalCount: nextTotalCount,
        pageCount: nextItems.length,
        collectionId: effectiveCollectionId,
      });
    } catch (e) {
      console.error("[ArtworksDashboard] fetchArtworks failed:", e);
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    user,
    page,
    dArtist,
    dMin,
    dMax,
    parsedTags,
    dLocation,
    dCondition,
    dInventory,
    dCategory,
    dMedium,
    sort,
    shouldUseCollectionScope,
    effectiveCollectionId,
    locations,
    // onStatsChange retiré des deps — géré via ref pour éviter la boucle
  ]);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    loadMediums();
  }, [loadMediums]);

  useEffect(() => {
    fetchArtworks();
  }, [fetchArtworks]);

  useEffect(() => setCollectionId(initialCollectionId), [initialCollectionId]);

  const handleImportSuccess = useCallback(() => {
    try {
      const maybePromise = onImportDone?.();
      if (maybePromise && typeof maybePromise.then === "function") {
        maybePromise.catch((e) =>
          console.error("[ArtworksDashboard] onImportDone failed:", e)
        );
      }
    } catch (e) {
      console.error("[ArtworksDashboard] onImportDone threw:", e);
    }
  }, [onImportDone]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-serif">
          {t("artworksDashboard.title")}
        </h1>

        {!isSharedCollection && (
          <div className="flex gap-3">
            <Link
              to="/add"
              state={{ preselectedCollectionId: effectiveCollectionId }}
              className="btn-artsy btn-primary-artsy"
            >
              {t("artworksDashboard.addArtwork")}
            </Link>

            {canUseImport(profile) ? (
              <a
                href="#import-premium"
                className="btn-artsy btn-secondary-artsy"
              >
                {t("artworksDashboard.importPremium")}
              </a>
            ) : (
              <Link
                to="/support/import"
                className="btn-artsy btn-secondary-artsy"
              >
                {t("artworksDashboard.importWithExpert")}
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
        <div className="grid grid-cols-1 lg:grid-cols-9 gap-4 mb-4">
          {/* Collection filter */}
          {!hideCollectionFilter && (
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t("artworksDashboard.filter.collection")}
              </label>
              <select
                value={collectionId || ""}
                onChange={(e) =>
                  setCollectionId(e.target.value || null)
                }
                className="form-control-artsy w-full"
              >
                <option value="">
                  {t("artworksDashboard.filter.allCollections")}
                </option>
                {collections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Artist */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              {t("artworksDashboard.filter.artist")}
            </label>
            <input
              type="text"
              placeholder={t("artworksDashboard.filter.artistPlaceholder")}
              value={artist}
              onChange={(e) => setArtist(e.target.value)}
              className="form-control-artsy w-full"
            />
          </div>


          {/* Catégorie */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              {t("artworksDashboard.filter.category", { defaultValue: "Medium" })}
            </label>
            <select
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
                setFilterMedium(""); // reset medium quand catégorie change
              }}
              className="form-control-artsy w-full"
            >
              <option value="">{t("artworksDashboard.filter.allFemale", { defaultValue: "Toutes" })}</option>
              {MEDIUM_CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>{getMediumCategoryLabelI18n(cat.value, t)}</option>
              ))}
            </select>
          </div>

          {/* Techniques — se restreint à la catégorie sélectionnée */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              {t("artworksDashboard.filter.medium", { defaultValue: "Techniques" })}
            </label>
            <select
              value={filterMedium}
              onChange={(e) => setFilterMedium(e.target.value)}
              className="form-control-artsy w-full"
            >
              <option value="">{t("artworksDashboard.filter.allMale", { defaultValue: "Tous" })}</option>
              {mediumOptions.map((medium) => (
                <option key={medium} value={medium}>{getMediumLabel(medium, t)}</option>
              ))}
            </select>
          </div>

          {/* Réf. inventaire */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              {t("artworksDashboard.filter.inventory", { defaultValue: "Réf. inventaire" })}
            </label>
            <input
              type="text"
              placeholder={t("artworksDashboard.filter.inventoryPlaceholder", { defaultValue: "Ex : Z-094-20" })}
              value={filterInventory}
              onChange={(e) => setFilterInventory(e.target.value)}
              className="form-control-artsy w-full"
            />
          </div>

          {/* Min value */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              {t("artworksDashboard.filter.minValue")}
            </label>
            <input
              type="number"
              placeholder="Min"
              value={minValue}
              onChange={(e) => setMinValue(e.target.value)}
              className="form-control-artsy w-full"
            />
          </div>

          {/* Max value */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              {t("artworksDashboard.filter.maxValue")}
            </label>
            <input
              type="number"
              placeholder="Max"
              value={maxValue}
              onChange={(e) => setMaxValue(e.target.value)}
              className="form-control-artsy w-full"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              {t("artworksDashboard.filter.tags")}
            </label>
            <input
              type="text"
              placeholder={t("artworksDashboard.filter.tagsPlaceholder")}
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              className="form-control-artsy w-full"
            />
          </div>

          {/* Emplacement */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              {t("artworksDashboard.filter.location", { defaultValue: "Emplacement" })}
            </label>
            <select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              className="form-control-artsy w-full"
            >
              <option value="">{t("artworksDashboard.filter.allMale", { defaultValue: "Tous" })}</option>
              {locations.map((l) => (
                <option key={l.id} value={l.name}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>

          {/* État */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              {t("artworksDashboard.filter.condition", { defaultValue: "État" })}
            </label>
            <select
              value={filterCondition}
              onChange={(e) => setFilterCondition(e.target.value)}
              className="form-control-artsy w-full"
            >
              <option value="">
                {t("artworksDashboard.filter.conditionAll", { defaultValue: "Tous les états" })}
              </option>
              {CONDITION_FILTER_KEYS.map((key) => (
                <option key={key} value={key}>{getConditionLabel(key, t)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* --- Sorting + Views --- */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          {/* Sort */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">
              {t("artworksDashboard.sort.label")}
            </label>

            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="form-control-artsy"
            >
              <option value="date_desc">{t("artworksDashboard.sort.dateDesc")}</option>
              <option value="date_asc">{t("artworksDashboard.sort.dateAsc")}</option>
              <option value="value_desc">{t("artworksDashboard.sort.valueDesc")}</option>
              <option value="value_asc">{t("artworksDashboard.sort.valueAsc")}</option>
              <option value="title_asc">{t("artworksDashboard.sort.titleAsc")}</option>
              <option value="title_desc">{t("artworksDashboard.sort.titleDesc")}</option>
              <option value="inventory_asc">
                {t("artworksDashboard.sort.inventoryAsc", { defaultValue: "Réf. inventaire (A→Z)" })}
              </option>
            </select>
          </div>

          {/* Views */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 mr-2">
              {t("artworksDashboard.view.label")}
            </span>

            <button
              type="button"
              onClick={() => setView("list")}
              className={`p-2 rounded ${view === "list" ? "bg-black text-white" : "bg-white text-gray-600 border"}`}
              title={t("artworksDashboard.view.list")}
            >
              ☰
            </button>

            <button
              type="button"
              onClick={() => setView("grid")}
              className={`p-2 rounded ${view === "grid" ? "bg-black text-white" : "bg-white text-gray-600 border"}`}
              title={t("artworksDashboard.view.grid")}
            >
              ▣
            </button>

            <button
              type="button"
              onClick={() => setView("mosaic")}
              className={`p-2 rounded ${view === "mosaic" ? "bg-black text-white" : "bg-white text-gray-600 border"}`}
              title={t("artworksDashboard.view.mosaic")}
            >
              ▦
            </button>
          </div>
        </div>
      </div>

      {/* Count */}
      <div className="mb-4 text-sm text-gray-600">
        {loading ? (
          t("artworksDashboard.loading")
        ) : (
          <>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} {t("artworksDashboard.of")} {totalCount} {t("artworksDashboard.artworks")}
          </>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">{t("artworksDashboard.loading")}</p>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <p className="text-gray-500 mb-4">
            {t("artworksDashboard.empty")}
          </p>
          {!isSharedCollection && (
            <Link
              to="/add"
              state={{ preselectedCollectionId: effectiveCollectionId }}
              className="btn-artsy btn-primary-artsy"
            >
              {t("artworksDashboard.addFirstArtwork")}
            </Link>
          )}
        </div>
      ) : (
        <>
          {view === "list" && <ListView items={items} routePrefix={routePrefix} />}
          {view === "grid" && <GridView items={items} routePrefix={routePrefix} />}
          {view === "mosaic" && <MosaicView items={items} routePrefix={routePrefix} />}
        </>
      )}

      {/* Pagination */}
      {totalCount > PAGE_SIZE && (
        <div className="flex justify-center gap-3 mt-8">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="btn-artsy btn-secondary-artsy disabled:opacity-50"
          >
            {t("artworksDashboard.pagination.previous")}
          </button>
          <span className="px-4 py-2 text-sm text-gray-600">
            {t("artworksDashboard.pagination.page", { page: page + 1, total: Math.ceil(totalCount / PAGE_SIZE) })}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={(page + 1) * PAGE_SIZE >= totalCount}
            className="btn-artsy btn-secondary-artsy disabled:opacity-50"
          >
            {t("artworksDashboard.pagination.next")}
          </button>
        </div>
      )}

      {/* Import Premium */}
      {!isSharedCollection && canUseImport(profile) && (
        <div id="import-premium" className="mt-12 bg-white border rounded-lg p-6">
          <h2 className="text-2xl font-serif mb-4">
            {t("artworksDashboard.importPremiumTitle")}
          </h2>
          <ImportCSVPremium
            collectionId={effectiveCollectionId}
            onSuccess={handleImportSuccess}
            onComplete={handleImportSuccess}
            onImported={(result) => {
              handleImportSuccess(result); // compat si ton composant appelle onImported(importedRows / summary)
            }}
          />

          {!effectiveCollectionId && (
            <p className="mt-3 text-sm text-red-600">
              {t("artworksDashboard.importNeedCollection")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ====== LIST VIEW ====== */
function ListView({ items, routePrefix = "" }) {
  const { t } = useTranslation();
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("artworksDashboard.table.artwork")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("artworksDashboard.table.artist")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("artworksDashboard.table.value")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("artworksDashboard.table.type")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("artworksDashboard.table.date")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("artworksDashboard.table.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((a) => (
              <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <img
                      loading="lazy"
                      src={getImageFromArtwork(a)}
                      alt={a.title}
                      className="h-10 w-10 rounded object-cover"
                      onError={(e) => {
                        e.currentTarget.src =
                          "/images/artworks/Vignette_oeuvre_Image_Not_Available.png";
                      }}
                    />
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {a.title || t("artworksDashboard.fallback.noTitle")}
                      </div>
                      {a.is_draft && (
                        <span style={{ display: "inline-block", marginTop: 2, background: "#c9a961", color: "#1a1a1a", fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 999, fontFamily: "system-ui, sans-serif" }}>
                          {t("artworksDashboard.draftBadge", { defaultValue: "Brouillon" })}
                        </span>
                      )}
                      {a.inventory_number && (
                        <div className="text-xs text-gray-400 italic mt-0.5">
                          {a.inventory_number}
                        </div>
                      )}
                      {a.lot_id && (
                        <span
                          style={{
                            fontSize: 9,
                            fontFamily: "system-ui, sans-serif",
                            fontWeight: 600,
                            letterSpacing: "0.05em",
                            color: "#c9a961",
                            border: "1px solid #c9a961",
                            borderRadius: 2,
                            padding: "1px 5px",
                          }}
                        >
                          ▣ LOT
                        </span>
                      )}
                    </div>
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {a.artist || "—"}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {formatEuroValue(getEstimatedDisplayValue(a))}
                  </div>
                  {getEstimatedDisplayValue(a) !== null && <ValuationStatusLine artwork={a} compact />}
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {a.typeOfArt ? getTypeOfArtLabel(a.typeOfArt, t) : "—"}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {a.created_at
                    ? new Date(a.created_at).toLocaleDateString()
                    : "—"}
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <Link
                    to={`${routePrefix}/artwork/${a.id}`}
                    className="text-black hover:text-gray-600 font-medium"
                  >
                    {t("artworksDashboard.action.open")}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===== GRID ===== */
function GridView({ items, routePrefix = "" }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {items.map((a) => (
        <div
          key={a.id}
          className="bg-white rounded-lg border overflow-hidden shadow-sm"
        >
          <Link
            to={`${routePrefix}/artwork/${a.id}`}
            style={{
              display: "block",
              height: "260px",
              backgroundColor: "#1a1a1a",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <img
              loading="lazy"
              src={getImageFromArtwork(a)}
              alt={a.title}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translateY(-50%) translateX(-50%)",
                maxWidth: "100%",
                maxHeight: "100%",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                transition: "transform 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform =
                  "translateY(-50%) translateX(-50%) scale(1.04)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform =
                  "translateY(-50%) translateX(-50%)";
              }}
              onError={(e) => {
                e.currentTarget.src =
                  "/images/artworks/Vignette_oeuvre_Image_Not_Available.png";
              }}
            />
            {a.is_draft && (
              <span
                style={{
                  position: "absolute",
                  top: 10,
                  left: 10,
                  zIndex: 2,
                  background: "#c9a961",
                  color: "#1a1a1a",
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "3px 9px",
                  borderRadius: 999,
                  fontFamily: "system-ui, sans-serif",
                  letterSpacing: "0.03em",
                }}
              >
                {t("artworksDashboard.draftBadge", { defaultValue: "Brouillon" })}
              </span>
            )}
          </Link>

          <div className="p-4">
            <h3 className="font-serif text-lg mb-1 truncate">
              {a.title || t("artworksDashboard.fallback.noTitle")}
            </h3>
            <p className="text-sm text-gray-600 mb-2 truncate">
              {a.artist || "—"}
            </p>
            {a.inventory_number && (
              <p className="text-xs text-gray-400 italic mb-1 truncate">
                {a.inventory_number}
              </p>
            )}
            {a.lot_id && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 10,
                  fontFamily: "system-ui, sans-serif",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  color: "#c9a961",
                  border: "1px solid #c9a961",
                  borderRadius: 2,
                  padding: "2px 6px",
                  marginBottom: 4,
                }}
              >
                ▣ LOT
              </span>
            )}

            <div className="flex items-center justify-between pt-3 border-t">
              <span className="text-sm font-medium">
                {formatEuroValue(getEstimatedDisplayValue(a))}
                {getEstimatedDisplayValue(a) !== null && <ValuationStatusLine artwork={a} compact />}
              </span>
              <Link
                to={`${routePrefix}/artwork/${a.id}`}
                className="text-sm text-black hover:text-gray-600 font-medium"
              >
                {t("artworksDashboard.action.open")}
              </Link>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ===== MOSAIC ===== */
function MosaicView({ items, routePrefix = "" }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
      {items.map((a) => (
        <Link
          key={a.id}
          to={`${routePrefix}/artwork/${a.id}`}
          className="group relative block overflow-hidden rounded-md border border-gray-200"
          style={{ paddingTop: "100%" }}
        >
          <img
            loading="lazy"
            src={getImageFromArtwork(a)}
            alt={a.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={(e) => {
              e.currentTarget.src =
                "/images/artworks/Vignette_oeuvre_Image_Not_Available.png";
            }}
          />

          <div
            className={`absolute bottom-0 left-0 p-2 w-full transition-opacity ${
              !a.title && a.inventory_number
                ? "bg-black/50 opacity-100"
                : "bg-black/60 opacity-0 group-hover:opacity-100"
            } text-white`}
          >
            <p className="text-xs font-medium truncate">
              {a.title || t("artworksDashboard.fallback.noTitle")}
            </p>
            {a.inventory_number && (
              <p className="text-xs text-gray-300 italic truncate">
                {a.inventory_number}
              </p>
            )}
          </div>
        </Link>
      ))}
    </div>
  );
}